import React, { useState } from "react";
import { BookOpen, FileText, Code, CheckCircle, Search } from "lucide-react";
import { Helmet } from "react-helmet-async";

export function Documentation() {
  const [searchQuery, setSearchQuery] = useState("");

  const docs = [
    {
      title: "How Offline-First Works",
      category: "Privacy & Tech",
      content: "All calculations, from bcrypt hashing to JSON formatting, are performed completely within your browser using client-side JavaScript. Because we do not run server-side proxies or database queries for your tool entries, your confidential keys, passwords, and database cells never leak outside your local machine."
    },
    {
      title: "Interactive CSV & Excel Grid Editors",
      category: "Data Tools",
      content: "Upload standard CSV or Excel (.xlsx) files directly. The app parses worksheets inside memory and builds a dynamic, responsive spreadsheet grid. You can double-click column headers to rename them, press '+ Add Row' or '+ Add Column', filter columns in real-time, and download your processed worksheets using the 'COMPILE & EXPORT' buttons."
    },
    {
      title: "Cryptographic Hash Generators",
      category: "Security & Crypto",
      content: "Instantly compile MD5, SHA-1, SHA-256, and SHA-512 hashes. You can also generate high-entropy passwords with custom inclusions (symbols, numerals, similar character filters), and compute secure Bcrypt hashes with adjustable rounds."
    },
    {
      title: "Code Formatting and Prettifying",
      category: "Dev Utilities",
      content: "Format messy HTML, CSS, JavaScript, and SQL blocks. We use client-side libraries to realign indentation and trim clutter. If you need to bundle files, you can also leverage our local Minifiers to compress code into one unified line."
    }
  ];

  const filteredDocs = docs.filter(
    d => d.title.toLowerCase().includes(searchQuery.toLowerCase()) || 
         d.content.toLowerCase().includes(searchQuery.toLowerCase())
  );

  return (
    <div className="max-w-4xl mx-auto py-10 px-4 sm:px-6 lg:px-8">
      <Helmet>
        <title>Documentation – 247 DevTools Hub</title>
        <meta name="description" content="Official documentation and user guide for 247 DevTools Hub tools, local-first architectures, and spreadsheet editors." />
      </Helmet>

      <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-6 sm:p-10 space-y-8">
        
        {/* Header */}
        <div className="border-b border-slate-100 pb-6">
          <div className="flex items-center gap-3 mb-2">
            <BookOpen className="w-8 h-8 text-blue-600" />
            <h1 className="text-3xl font-extrabold text-slate-900 tracking-tight">Documentation & User Guide</h1>
          </div>
          <p className="text-slate-500 text-sm">
            Everything you need to know about utilizing the offline-first web utilities in the Hub.
          </p>
        </div>

        {/* Local Search */}
        <div className="relative">
          <Search className="w-5 h-5 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input 
            type="text" 
            placeholder="Search documentation sections..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="w-full bg-slate-50 border border-slate-200 rounded-xl pl-10 pr-4 py-3 text-sm outline-none focus:bg-white focus:border-blue-500 focus:ring-1 focus:ring-blue-500 transition-all"
          />
        </div>

        {/* Sections */}
        <div className="space-y-8">
          {filteredDocs.map((doc, idx) => (
            <div key={idx} className="p-5 bg-slate-50 rounded-xl border border-slate-100 space-y-2">
              <div className="flex items-center justify-between">
                <span className="text-xs font-bold text-blue-600 uppercase tracking-wider">{doc.category}</span>
                <span className="text-xs text-slate-400">Section {idx + 1}</span>
              </div>
              <h3 className="text-lg font-bold text-slate-900">{doc.title}</h3>
              <p className="text-sm text-slate-600 leading-relaxed">{doc.content}</p>
            </div>
          ))}

          {filteredDocs.length === 0 && (
            <p className="text-center text-slate-400 text-sm py-6">No matching documentation topics found.</p>
          )}
        </div>

        {/* Feature Checkmarks */}
        <div className="border-t border-slate-100 pt-8 grid sm:grid-cols-2 gap-4">
          <div className="flex items-start gap-2.5">
            <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-slate-900 text-sm">Strict Privacy Audits</h4>
              <p className="text-xs text-slate-500 leading-normal">Zero external tracking, databases, or third-party server-side data logs.</p>
            </div>
          </div>
          <div className="flex items-start gap-2.5">
            <CheckCircle className="w-5 h-5 text-emerald-500 shrink-0 mt-0.5" />
            <div>
              <h4 className="font-semibold text-slate-900 text-sm">Standard Conformance</h4>
              <p className="text-xs text-slate-500 leading-normal">Complies with the latest standards for RFC cryptographic hashes, Base64, and CSV specifications.</p>
            </div>
          </div>
        </div>

      </div>
    </div>
  );
}
